import { Code, ConnectError } from '@bufbuild/connect';

import { DocumentTemplateIssue } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';

import type {
  DictationRefusal,
  PartnerExportRefusal,
} from './transcriptorTypes';

/**
 * A failed scribe call. `message` is the text scribe sent, because that is what
 * the UI renders; ConnectError.message would prefix it with the status code.
 * It is empty when scribe sent an error type this client does not render, so a
 * caller showing it must fall back to its own message on an empty string.
 */
export class ScribeError extends Error {
  constructor(
    message: string | undefined,
    public readonly code: Code,
    public readonly metadata: Headers,
    options?: { cause?: unknown },
  ) {
    super(message, options);
    this.name = 'ScribeError';
  }
}

// The backend's shared error types serialize themselves as JSON, so a scribe
// message can arrive as an object rather than a sentence.
type ServiceError =
  | { type: 'NotFoundError'; entity: string }
  | { type: 'AlreadyExistError'; entity: string }
  | { type: 'PermissionDeniedError'; reason: string }
  | { type: 'RequiredArgumentMissingError'; argument: string }
  | {
      type: 'InvalidArgumentValueError';
      argument: string;
      actualValue: string;
    }
  | { type: 'BadRequestError'; reason: string }
  | { type: 'UnprocessableEntityError'; reason: string };

const readServiceError = (message: string): ServiceError | null => {
  if (!message.startsWith('{')) {
    return null;
  }

  try {
    const parsed = JSON.parse(message) as ServiceError;

    return typeof parsed?.type === 'string' ? parsed : null;
  } catch {
    return null;
  }
};

// ServiceError describes what the backend may send, not what it will: the type
// is asserted from arbitrary JSON, and svc_errors has types this switch does not
// render (InternalServerError carries the raw Go failure, UserBadRequestError a
// code with no mapping here). Those yield undefined, and the caller falls back
// to its own localized message.
const serviceErrorText = (error: ServiceError): string | undefined => {
  switch (error.type) {
    case 'NotFoundError':
      return `${error.entity} not found`;
    case 'AlreadyExistError':
      return `${error.entity} already exists`;
    case 'PermissionDeniedError':
      return error.reason;
    case 'RequiredArgumentMissingError':
      return `${error.argument} is required`;
    case 'InvalidArgumentValueError':
      return error.actualValue
        ? `invalid ${error.argument}: ${error.actualValue}`
        : `invalid ${error.argument}`;
    case 'BadRequestError':
    case 'UnprocessableEntityError':
      return error.reason;
  }
};

const scribeMessage = (rawMessage: string): string | undefined => {
  const serviceError = readServiceError(rawMessage);

  return serviceError ? serviceErrorText(serviceError) : rawMessage;
};

export const callScribe = async <T>(call: Promise<T>): Promise<T> => {
  try {
    return await call;
  } catch (error) {
    if (!(error instanceof ConnectError)) {
      throw error;
    }

    throw new ScribeError(
      scribeMessage(error.rawMessage),
      error.code,
      error.metadata,
      { cause: error },
    );
  }
};

// Only a scribe refusal carries text meant for the user; transport failures
// (for example Safari's "Load failed") do not.
export const getScribeErrorText = (error: unknown) =>
  error instanceof ScribeError && error.message.trim()
    ? error.message
    : undefined;

// Answers use optimistic concurrency: an edit based on an outdated revision is
// refused with Aborted, and the client refetches and reapplies it. Scribe's
// other refusals of an edit are FailedPrecondition and are not retried.
export const isStaleAnswersRevisionError = (error: unknown) =>
  error instanceof ScribeError && error.code === Code.Aborted;

// Sent verbatim by scribe (service/catalog/document_templates.go,
// errSeriesMoved) when it refuses a template version based on a version that
// is no longer its series' current one, or that a concurrent save is
// replacing. Its other refusals of a template write, such as block notes
// being disabled, carry the same code.
const SCRIBE_SERIES_MOVED =
  /document template series \S+: (?:the current version is \S+, not \S+|a concurrent save is replacing version \S+): reload it and save again/;
const SCRIBE_SERIES_BUSY =
  /document template series \S+: a concurrent save is replacing version \S+: reload it and save again/;

const isSeriesRefusal = (error: unknown, refusal: RegExp) =>
  error instanceof ScribeError &&
  error.code === Code.FailedPrecondition &&
  refusal.test(error.message);

// Another save replaced the version the refused one was based on, or is
// replacing it.
export const isTemplateSeriesMovedError = (error: unknown) =>
  isSeriesRefusal(error, SCRIBE_SERIES_MOVED);

// Another save is still replacing the version the refused one was based on:
// the series has not moved yet.
export const isTemplateSeriesBusyError = (error: unknown) =>
  isSeriesRefusal(error, SCRIBE_SERIES_BUSY);

// Sent verbatim by scribe (service/runtime/partner_export.go) when the pinned
// template is not a partner's; the export's own refusals start with their kind
// instead (pkg/partnertemplate.ExportError).
const SCRIBE_NOT_PARTNER_TEMPLATE =
  "the document's template was not converted from a partner's template";

export const getPartnerExportRefusal = (
  error: unknown,
): PartnerExportRefusal | undefined => {
  if (
    !(error instanceof ScribeError) ||
    error.code !== Code.FailedPrecondition
  ) {
    return undefined;
  }
  const [kind] = error.message.split(':', 1);
  switch (kind) {
    case 'not_partner_template':
      return 'notPartnerTemplate';
    case 'unsupported_format':
      return 'unsupportedFormat';
    case 'source_mismatch':
      return 'sourceMismatch';
    default:
      return error.message === SCRIBE_NOT_PARTNER_TEMPLATE
        ? 'notPartnerTemplate'
        : undefined;
  }
};

const DICTATION_REFUSALS: DictationRefusal[] = [
  'dictation_limit_reached',
  'dictation_unavailable',
  'dictation_closed',
];

// Scribe names a dictation refusal in the error text of both transports: the
// REST body's `error` (400 or 409) and a Connect FailedPrecondition. The
// refusal also reaches callers as a rejected thunk's plain object or as the
// uploader's error string, so the text is read from any of them.
export const getDictationRefusal = (
  error: unknown,
): DictationRefusal | undefined => {
  const text =
    typeof error === 'object' && error !== null && 'message' in error
      ? (error as { message?: unknown }).message
      : error;
  return typeof text === 'string'
    ? DICTATION_REFUSALS.find((refusal) =>
        new RegExp(`\\b${refusal}\\b`).test(text),
      )
    : undefined;
};

// A refused template or note-list write carries one issue per problem as a
// Connect error detail, so the editor marks where each one is.
export const getTemplateIssues = (error: unknown): DocumentTemplateIssue[] =>
  error instanceof ScribeError && error.cause instanceof ConnectError
    ? error.cause.findDetails(DocumentTemplateIssue)
    : [];
